import React, { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  IconButton,
  TextField,
  Box,
  Collapse,
  Button,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';

const formatDate = (date) => {
  if (!date) return '';
  return format(new Date(date), 'MMM d, yyyy h:mm a');
};

const getItemMetadataText = (item) => {
  const lines = [
    // Combine creator info into one line
    item.createdBy && item.createdAt && 
      `Created by ${item.createdBy.firstName} ${item.createdBy.lastName} on ${formatDate(item.createdAt)}`,
    
    // Combine modifier info into one line
    item.lastModifiedBy && item.lastModifiedAt && 
      `Modified by ${item.lastModifiedBy.firstName} ${item.lastModifiedBy.lastName} on ${formatDate(item.lastModifiedAt)}`,
    
    // Combine completion info into one line
    item.completedBy && item.completedAt && 
      `Completed by ${item.completedBy.firstName} ${item.completedBy.lastName} on ${formatDate(item.completedAt)}`
  ].filter(Boolean);
  
  return lines.join('\n');
};

function TaskChecklist({ items, onReorder, onToggle, onUpdate, onDelete }) {
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);
    
    onReorder(newItems);
  };

  const handleStartEdit = (item) => {
    setEditingId(item._id);
    setEditText(item.text);
    setEditDescription(item.description || '');
  };

  const handleSaveEdit = (itemId) => {
    onUpdate(itemId, {
      text: editText.trim(),
      description: editDescription.trim() || null
    });
    setEditingId(null);
    setEditText('');
    setEditDescription('');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText('');
    setEditDescription('');
  };

  const toggleExpand = (itemId) => {
    setExpandedId(expandedId === itemId ? null : itemId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Droppable droppableId="checklist">
        {(provided) => (
          <List ref={provided.innerRef} {...provided.droppableProps}>
            {items.map((item, index) => (
              <Draggable key={item._id} draggableId={item._id} index={index}>
                {(provided, snapshot) => (
                  <>
                    <ListItem
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      sx={{
                        bgcolor: snapshot.isDragging ? 'grey.100' : 'transparent',
                        '&:hover': { bgcolor: 'grey.50' },
                      }}
                    >
                      <ListItemIcon {...provided.dragHandleProps}>
                        <DragIndicatorIcon />
                      </ListItemIcon>
                      <ListItemIcon>
                        <Checkbox
                          edge="start"
                          checked={item.completed}
                          onChange={() => onToggle(item._id)}
                        />
                      </ListItemIcon>
                      
                      {editingId === item._id ? (
                        <Box sx={{ flex: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                          <TextField
                            fullWidth
                            size="small"
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            autoFocus
                          />
                          <IconButton 
                            size="small" 
                            onClick={() => handleSaveEdit(item._id)}
                            disabled={!editText.trim()}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={handleCancelEdit}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      ) : (
                        <>
                          <ListItemText 
                            primary={item.text}
                            secondary={
                              <>
                                {item.description && !expandedId && (
                                  <Typography
                                    variant="caption"
                                    component="span"
                                    sx={{ 
                                      display: '-webkit-box',
                                      WebkitLineClamp: 1,
                                      WebkitBoxOrient: 'vertical',
                                      overflow: 'hidden'
                                    }}
                                  >
                                    {item.description}
                                  </Typography>
                                )}
                              </>
                            }
                            sx={{ 
                              textDecoration: item.completed ? 'line-through' : 'none',
                              color: item.completed ? 'text.secondary' : 'text.primary'
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip
                              title={getItemMetadataText(item)}
                              placement="top"
                              componentsProps={{
                                tooltip: {
                                  sx: { 
                                    whiteSpace: 'pre-line',
                                    maxWidth: 'none'
                                  }
                                }
                              }}
                            >
                              <IconButton size="small">
                                <InfoIcon fontSize="small" color="action" />
                              </IconButton>
                            </Tooltip>
                            {item.description && (
                              <IconButton 
                                size="small" 
                                onClick={() => toggleExpand(item._id)}
                              >
                                {expandedId === item._id ? 
                                  <ExpandLessIcon fontSize="small" /> : 
                                  <ExpandMoreIcon fontSize="small" />
                                }
                              </IconButton>
                            )}
                            <IconButton 
                              size="small" 
                              onClick={() => handleStartEdit(item)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton 
                              size="small" 
                              onClick={() => onDelete(item._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </>
                      )}
                    </ListItem>
                    
                    {/* Description editing and expanded view */}
                    <Collapse in={editingId === item._id || expandedId === item._id}>
                      <Box sx={{ pl: 9, pr: 2, pb: 2 }}>
                        {editingId === item._id ? (
                          <TextField
                            fullWidth
                            size="small"
                            multiline
                            rows={2}
                            placeholder="Add description (optional)"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                          />
                        ) : (
                          item.description && (
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ whiteSpace: 'pre-wrap' }}
                            >
                              {item.description}
                            </Typography>
                          )
                        )}
                      </Box>
                    </Collapse>
                  </>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </DragDropContext>
  );
}

export default TaskChecklist; 